import { Style } from '@capacitor/status-bar';
import { action, flow, observable, runInAction } from 'mobx';
import { GenericFunction } from '../../base/@types';
import { ColorCodedState, Renderable } from '../../base/@types/ui.types';
import { IconName } from '../../base/components/Symbols/iconDefs';
import { DisplayMode } from '../../base/constants/DisplayMode.enum';
import Toast from '../../base/constructors/toast.constructor';
import { NativeController } from './native.controller';
import { IToast, ToastConfig } from './ui.controller.types';

export const makeToastController = () => {
  
  const c = observable({

    uiController: undefined as {
      displayMode: DisplayMode,
      NATIVE: NativeController,
    } | undefined,
    
    toasts: [] as IToast[],

    present: action((t: ToastConfig) => {
      return new Promise<Toast>(flow(function * (resolve) {
        const config = { ...t }
        if (config.wait) yield config.wait();
        const toast = new Toast(config, c);
        if (c.uiController?.displayMode === 'phone') {
          c.uiController.NATIVE.setStatusBarStyle(Style.Light);
        }
        runInAction(() => c.toasts.push(toast));
        resolve(toast);
      }))
    }),

    toast: action((
      message: Renderable,
      colorCodedState?: ColorCodedState,
      timeout: number = 5000,
      icon?: IconName,
      action?: GenericFunction
    ) => c.present({heading: message, colorCodedState, timeout, icon, action})),

    positive: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.positive, timeout, icon, action)),
    success: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.positive, timeout, icon, action)),
    neutral: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.neutral, timeout, icon, action)),
    attention: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.attention, timeout, icon, action)),
    alert: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.alert, timeout, icon, action)),
    error: action((message: Renderable, timeout?: number, icon?: IconName, action?: GenericFunction) => c.toast(message, ColorCodedState.error, timeout, icon, action)),

    dismiss: action((t: IToast | string) => {
      const index = c.toasts.findIndex(toast => typeof t === 'string' ? toast.config.name === t : toast === t);
      const toast = c.toasts[index];
      if (c.uiController?.displayMode === 'phone') {
        if (c.toasts.length === 1) {
          c.uiController.NATIVE.setStatusBarStyle(Style.Dark);
        }
      }
      if (toast) {
        if (toast.status === 'closed') {
          c.toasts.splice(index, 1);
        } else {
          toast.close();
        }
      }
    }),
    get hasToasts(): boolean {
      return c.toasts.filter(o => o.status === 'opened').length > 0;
    },
    hasToast: (id: string) => {
      return c.toasts.find(t => t.id === id);
    },
    reset: action(() => {
      c.toasts.forEach(t => t.close());
    })
  })

  return c;

}

export type ToastController = ReturnType<typeof makeToastController>;